import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Button, Select, Alert, message, Space, Typography, Table, Radio } from 'antd';
import { SearchOutlined, SwapOutlined, UserOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { getLeadByPhoneNumber, getLeadById, assignLead, getSiteVisitForms, getSiteVisitFormsById,getKernelParams } from '../services/apiService';
import authService from '../services/authService';
import { getProjectName, getAssignedUser } from '../examples/LeadUsageExample';

const { Title, Text } = Typography;

export default function LeadReassign() {
  const { companyId: paramCompanyId } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [leadData, setLeadData] = useState(null);
  const [multipleLeads, setMultipleLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [showLeadSelection, setShowLeadSelection] = useState(false);
  const [companyId, setCompanyId] = useState(paramCompanyId);
  const [managersWithEmails, setManagersWithEmails] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const fetchUserAndCompany = async () => {
      try {
        const user = await authService.getUserDetails();
        setCurrentUser(user);
        
        const effectiveCompanyId = companyId || user?.company_id;
        
        if (!companyId && user?.company_id) {
          setCompanyId(user.company_id);
        }

        // Load managers from form meta
        if (effectiveCompanyId) {
          try {
            // Get forms - use user-specific endpoint if not superadmin
            const isSuperAdmin = user?.role_id === 'superadmin' || user?.role_id === 'Super Administrator';
            const userId = !isSuperAdmin ? (user?.user_id || user?.id) : null;
            
            const formsResponse = await getSiteVisitForms(effectiveCompanyId, userId);
            const forms = Array.isArray(formsResponse) 
              ? formsResponse 
              : Array.isArray(formsResponse?.data) 
                ? formsResponse.data 
                : [];
            
            ////console.log('Forms response:', forms);
            
            // Find the first active form
            const activeForm = forms.find(form => form?.meta?.active === true);
            
            if (activeForm) {
              const formId = activeForm.meta?.form_id || activeForm.id;
              //console.log('Active form found:', formId);
              
              // Get form details to extract managers from meta
              const formDetails = await getSiteVisitFormsById(effectiveCompanyId, formId);
              //console.log('Form details:', formDetails);
              
              // Extract managers from fields where type is "object"
              if (formDetails && formDetails?.fields) {
                const managerFields = [];
                const fields = formDetails.fields;
                
                //console.log('Form fields:', fields);
                
                const kernelParams = await getKernelParams(effectiveCompanyId, formId); 
                console.log('kernelParams',kernelParams);
                // Check fields for object type with manager label
                if (Array.isArray(fields)) {
                  fields.forEach(field => {
                    // Check if field type is "object" and label contains "Manager"
                    if (field.type === 'object' && field.label && field.label.toLowerCase().includes('manager')) {
                      //console.log('Found manager field:', field.label, field);
                      
                      if (field.options && Array.isArray(field.options)) {
                        field.options.forEach(option => {
                          // Check for email_id (from API) or email
                          const email = option.email_id || option.email;
                          const name = option.name;
                          
                          if (email && name) {
                            managerFields.push({
                              name: name,
                              email: email,
                              label: field.label,
                              fieldKey: field.name,
                              user_id: option.user_id,
                              phone_number: option.phone_number
                            });
                          }
                        });
                      }
                    }
                  });
                }
                
                //console.log('Extracted managers from form fields:', managerFields);
                
                if (managerFields.length > 0) {
                  // Remove duplicates based on email
                  const uniqueManagers = managerFields.filter((manager, index, self) =>
                    index === self.findIndex((m) => m.email === manager.email)
                  );
                  setManagersWithEmails(uniqueManagers);
                  //console.log('Unique managers set:', uniqueManagers);
                } else {
                  console.warn('No managers found in form fields');
                }
              }
            } else {
              console.warn('No active form found');
            }
          } catch (err) {
            console.error('Failed to load managers from form:', err);
          }
        }
      } catch (err) {
        console.error('Failed to fetch user details:', err);
      }
    };

    fetchUserAndCompany();
  }, [companyId]);

  const handleSearch = async (values) => {
    const { phoneNumber } = values;
    
    if (!phoneNumber || phoneNumber.length !== 10) {
      message.error('Please enter a valid 10-digit phone number');
      return;
    }

    setSearching(true);
    setLeadData(null);
    setMultipleLeads([]);
    setShowLeadSelection(false);
    setSelectedLeadId(null);

    try {
      const searchResponse = await getLeadByPhoneNumber(companyId, phoneNumber);
      
      console.log('Search response:', searchResponse);
      
      if (searchResponse.data && searchResponse.data.length > 0) {
        const leads = searchResponse.data;
        
        // Log the first lead structure for debugging
        console.log('Lead data structure:', leads[0]);
        console.log('Available fields in lead:', Object.keys(leads[0]));
        
        if (leads.length === 1) {
          // Single lead found - proceed with existing logic
          await handleSingleLeadFound(leads[0]);
        } else {
          // Multiple leads found - show selection
          console.log(`Found ${leads.length} leads with phone number ${phoneNumber}`);
          setMultipleLeads(leads);
          setShowLeadSelection(true);
          message.info(`Found ${leads.length} leads with this phone number. Please select one to reassign.`);
        }
      } else {
        message.warning('No lead found with this phone number');
      }
    } catch (error) {
      console.error('Error searching lead:', error);
      message.error(error.message || 'Failed to search lead');
    } finally {
      setSearching(false);
    }
  };

  const handleSingleLeadFound = async (searchResult) => {
    const leadId = searchResult.lead_id;
    
    // Get current assigned manager from user_assigned object in search response
    const userAssignedObject = searchResult.user_assigned;
    const currentAssignedManager = userAssignedObject?.name || null;
    
    console.log('Search result structure:', searchResult);
    console.log('Available fields in search result:', Object.keys(searchResult));
    console.log('User assigned object:', userAssignedObject);
    console.log('Current assigned manager name:', currentAssignedManager);
    
    if (leadId) {
      const leadDetails = await getLeadById(companyId, leadId);
      console.log('Lead details response:', leadDetails);
      console.log('Available fields in lead details:', Object.keys(leadDetails));
      console.log('Lead fields object:', leadDetails.lead_fields);
      console.log('Project information:', {
        project_name: leadDetails.project_name,
        project: leadDetails.project,
        campaign_name: leadDetails.campaign_name,
        lead_fields_project: leadDetails.lead_fields?.project_name || leadDetails.lead_fields?.project
      });
      
      if (leadDetails) {
        // Create userAssignedObject from assigned_to
        let userAssignedObject = null;
        if (leadDetails.assigned_to) {
          userAssignedObject = {
            user_id: leadDetails.assigned_to.id,
            id: leadDetails.assigned_to.id,
            name: leadDetails.assigned_to.name,
            email_id: leadDetails.assigned_to.email || leadDetails.assigned_to.user_phone || '',
            phone_number: leadDetails.assigned_to.user_phone || '',
            tracking_number: leadDetails.assigned_to.tracking_number || ''
          };
          console.log('Created userAssignedObject from assigned_to:', userAssignedObject);
        }
        
        setLeadData(leadDetails);
        setShowLeadSelection(false);
        
        // Get assigned user info using helper function
        const assignedUserInfo = getAssignedUser(leadDetails);
        
        // Pre-fill current Sales Manager
        form.setFieldValue('currentManager', assignedUserInfo.name);
        form.setFieldValue('currentManagerEmail', assignedUserInfo.email);
        console.log('Set current manager to:', assignedUserInfo.name, assignedUserInfo.email);
        
        message.success('Lead found successfully');
      }
    }
  };

  const handleLeadSelection = async (leadId) => {
    setSelectedLeadId(leadId);
    const selectedLead = multipleLeads.find(lead => lead.lead_id === leadId);
    if (selectedLead) {
      await handleSingleLeadFound(selectedLead);
    }
  };

  const leadSelectionColumns = [
    {
      title: 'Select',
      dataIndex: 'lead_id',
      key: 'select',
      width: 80,
      render: (leadId) => (
        <Radio
          checked={selectedLeadId === leadId}
          onChange={() => handleLeadSelection(leadId)}
        />
      ),
    },
    {
      title: 'Lead ID',
      dataIndex: 'lead_id',
      key: 'lead_id',
      width: 120,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => name || 'N/A',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email || 'N/A',
    },
    {
      title: 'Project Name',
      dataIndex: 'project_name',
      key: 'project_name',
      render: (projectName, record) => getProjectName(record),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (source, record) => source || record.lead_source || 'N/A',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status, record) => status || record.lead_status || 'N/A',
    },
    {
      title: 'Current Manager',
      dataIndex: 'user_assigned',
      key: 'current_manager',
      render: (userAssigned, record) => {
        const assignedUser = getAssignedUser(record);
        return assignedUser.name;
      },
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A',
    },
  ];

  const handleBackToSearch = () => {
    setLeadData(null);
    setMultipleLeads([]);
    setShowLeadSelection(false);
    setSelectedLeadId(null);
    form.resetFields(['currentManager', 'currentManagerEmail', 'newManagerEmail']);
  };

  const handleReassign = async (values) => {
    const { newManagerEmail } = values;
    
    if (!leadData) {
      message.error('Please search for a lead first');
      return;
    }

    if (!newManagerEmail) {
      message.error('Please select a new Sales Manager');
      return;
    }

    setLoading(true);

    try {
      const leadId = leadData.lead_id;
      
      //console.log('Assigning lead:', leadId, 'to manager email:', newManagerEmail);

      // Use the new assign API
      await assignLead(companyId, leadId, newManagerEmail);
      
      message.success('Lead reassigned successfully to ' + newManagerEmail);
      
      // Reset form and lead data
      form.resetFields();
      setLeadData(null);
      setMultipleLeads([]);
      setShowLeadSelection(false);
      setSelectedLeadId(null);
    } catch (error) {
      console.error('Error reassigning lead:', error);
      message.error(error.message || 'Failed to reassign lead');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Card>
        <Title level={3}>
          <SwapOutlined /> Lead Re-assign
        </Title>
        <Text type="secondary">
          Search for a lead by phone number and reassign to a different Sales Manager
        </Text>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSearch}
          style={{ marginTop: 24 }}
        >
          <Form.Item
            label="Phone Number"
            name="phoneNumber"
            rules={[
              { required: true, message: 'Please enter phone number' },
              { pattern: /^[0-9]{10}$/, message: 'Please enter a valid 10-digit phone number' }
            ]}
          >
            <Input
              placeholder="Enter 10-digit phone number"
              maxLength={10}
              suffix={
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  htmlType="submit"
                  loading={searching}
                >
                  Search
                </Button>
              }
            />
          </Form.Item>
        </Form>

        {showLeadSelection && multipleLeads.length > 0 && (
          <Card
            type="inner"
            title={
              <Space>
                <UserOutlined />
                <span>Multiple Leads Found - Select One to Reassign</span>
              </Space>
            }
            style={{ marginTop: 24 }}
          >
            <Alert
              message={`Found ${multipleLeads.length} leads with this phone number`}
              description="Please select the lead you want to reassign by clicking the radio button."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            
            <Table
              dataSource={multipleLeads}
              columns={leadSelectionColumns}
              rowKey="lead_id"
              pagination={false}
              size="small"
              scroll={{ x: 1200 }}
              rowClassName={(record) => 
                selectedLeadId === record.lead_id ? 'selected-lead-row' : ''
              }
              onRow={(record) => ({
                onClick: () => handleLeadSelection(record.lead_id),
                style: { 
                  cursor: 'pointer',
                  backgroundColor: selectedLeadId === record.lead_id ? '#e6f7ff' : 'transparent'
                }
              })}
            />
            
            <div style={{ marginTop: 16, textAlign: 'center' }}>
              <Button onClick={handleBackToSearch}>
                Back to Search
              </Button>
            </div>
          </Card>
        )}

        {leadData && !showLeadSelection && (
          <Card
            type="inner"
            title={
              <Space>
                <span>Lead Details</span>
                <Button 
                  size="small" 
                  onClick={handleBackToSearch}
                  style={{ marginLeft: 'auto' }}
                >
                  Back to Search
                </Button>
              </Space>
            }
            style={{ marginTop: 24 }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong>Lead ID: </Text>
                <Text code>{leadData.lead_id || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Name: </Text>
                <Text>{leadData.name || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Email: </Text>
                <Text>{leadData.email || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Phone: </Text>
                <Text>{leadData.phones?.[0]?.phone || leadData.phone || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Project Name: </Text>
                <Text>{getProjectName(leadData)}</Text>
              </div>
              <div>
                <Text strong>Lead Source: </Text>
                <Text>{leadData.source || leadData.lead_source || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Status: </Text>
                <Text>{leadData.status || leadData.lead_status || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Created Date: </Text>
                <Text>{leadData.created_at ? new Date(leadData.created_at).toLocaleDateString() : 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Last Updated: </Text>
                <Text>{leadData.updated_at ? new Date(leadData.updated_at).toLocaleDateString() : 'N/A'}</Text>
              </div>

              <Form
                form={form}
                layout="vertical"
                onFinish={handleReassign}
                style={{ marginTop: 16 }}
              >
                <Form.Item
                  label="Current Assignment"
                  name="currentManager"
                >
                  <Input disabled placeholder="Not assigned" />
                </Form.Item>


                <Form.Item
                  label="New Assignment"
                  name="newManagerEmail"
                  rules={[
                    { required: true, message: 'Please select a manager' }
                  ]}
                  extra="Select the user you want to assign this lead to"
                >
                  {managersWithEmails.length > 0 ? (
                    <Select
                      showSearch
                      placeholder="Select manager"
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      options={managersWithEmails.map(manager => ({
                        label: `${manager.name} (${manager.label})`,
                        value: manager.email,
                        data: manager
                      }))}
                    />
                  ) : (
                    <Input
                      placeholder="manager@example.com"
                      type="email"
                    />
                  )}
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<SwapOutlined />}
                    block
                  >
                    Reassign Lead
                  </Button>
                </Form.Item>
              </Form>
            </Space>
          </Card>
        )}

        {!leadData && !showLeadSelection && (
          <Alert
            message="No Lead Selected"
            description="Search for a lead using the phone number above to begin reassignment"
            type="info"
            showIcon
            style={{ marginTop: 24 }}
          />
        )}
      </Card>
    </div>
  );
}
